import { useTranslation } from "react-i18next";
import cn from "classnames";
import { RadioButton } from "@/shared/ui";
import type { BudgetSection } from "../../../types";
import { formatCurrencyNoDecimals, clampProgressWidth, parseMoney, removeSectionNumber, SECTION_ICONS } from "../../../utils";
import { getSectionColorClasses, getSubcategoryColorClasses } from "../../../utils/consts";
import { getLocalizedText } from "../../../utils/localization";
import { calculateRemaining } from "../../../utils/calculations";

interface Props {
  section: BudgetSection;
  isActive: boolean;
  onClick: (section: BudgetSection) => void;
  hidePlanned?: boolean;
}

export default function SectionCard({ section, isActive, onClick, hidePlanned = false }: Props) {
  const { t, i18n } = useTranslation("FinancePage");
  const locale = i18n.language || "ru";
  const { fill } = getSectionColorClasses(section.id);
  const limitValue = section.planned_total;
  const remaining = calculateRemaining(limitValue, section.total);
  const Icon = SECTION_ICONS[section.id];

  const limitTotal = parseMoney(limitValue);
  const subcategorySegments = section.subcategories
    .map((sub, index) => {
      const subTotal = parseMoney(sub.total);
      const share = limitTotal > 0 ? (subTotal / limitTotal) * 100 : 0;
      return {
        id: sub.id,
        share: clampProgressWidth(share),
        fillClass: getSubcategoryColorClasses(sub.id, index).fill,
      };
    })
    .filter((seg) => seg.share > 0);

  return (
    <button
      type="button"
      onClick={() => onClick(section)}
      className={cn(
        "radius-lg border p-4 text-left w-full outline-none",
        isActive
          ? "border-2 surface-brand-stroke surface-brand-subtle"
          : "surface-base-fill surface-base-stroke hover:shadow-sm cursor-pointer",
      )}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className={cn("w-8 h-8 radius-xs flex shrink-0 items-center justify-center radial-glow", fill)}>
            {Icon && <Icon size={16} color="currentColor" className="text-white" />}
          </div>
          <span className="text-label-sm content-action-neutral">
            {removeSectionNumber(getLocalizedText(section.name, locale))}
          </span>
        </div>
        <RadioButton checked={isActive} readOnly />
      </div>

      <div className="flex items-baseline justify-between mb-2">
        <div className="flex items-end gap-0.5">
          <span className="text-display-2xs content-base-primary">
            {formatCurrencyNoDecimals(section.total, false)}
          </span>
          <span className="text-heading-md content-base-secondary">₸</span>
        </div>
        {!hidePlanned && (
          <span className="text-label-sm content-base-low">{section.execution_percentage.toFixed(0)}%</span>
        )}
      </div>

      {!hidePlanned && (
        <div className="flex gap-0.5 mb-2">
          {subcategorySegments.map((seg) => (
            <div
              key={seg.id}
              className={cn("h-1.5 radius-2xs", seg.fillClass)}
              style={{ width: `${seg.share}%` }}
            />
          ))}
          <div className="h-1.5 radius-2xs surface-component-stroke-fill flex-1" />
        </div>
      )}

      {!hidePlanned && (
        <div className="flex justify-between text-label-sm content-base-secondary">
          <div className="flex flex-col gap-1">
            <span className="text-label-xs content-base-secondary">{t("section.limit")}</span>
            <span className="content-base-primary text-body-bold-sm">
              {formatCurrencyNoDecimals(limitValue)}
            </span>
          </div>
          <div className="flex flex-col items-end">
            <span className="text-label-xs content-base-secondary">{t("section.remaining")}</span>
            <span className="content-base-primary text-body-bold-sm">{formatCurrencyNoDecimals(remaining)}</span>
          </div>
        </div>
      )}
    </button>
  );
}
